/**
 * Add a team member in one flow (DNKADM-0491; dink-api DNKAPI-0713).
 *
 * `POST /v2/admin/team-members` invites the person (saas-auth), gives them their locations
 * and, for a coach, creates the bookable staff record, puts it on the services they teach and
 * sets up their booking calendar. Every step's outcome rides the `200`, so the drawer can show
 * a partial success (the coach exists, the calendar did not link) as such.
 *
 * Bearer-only `/v2/admin/**` call via `request()`. Tenant and actor are server-derived from the
 * JWT; the caller sends only what the form chose. Who may add whom (Owner: any; Manager: Front
 * desk and Coach) and which locations/services are inside the caller's access are enforced by
 * dink-api (`403 ROLE_NOT_ALLOWED` / `LOCATION_ACCESS_DENIED`); the drawer only stops offering
 * what would be refused.
 *
 * Idempotent on `idempotencyKey` (1-128 of `[A-Za-z0-9._:-]`): a repeat of the same key answers
 * the stored result and writes nothing twice. The key is minted by the drawer per Add it means
 * once (the `AddMemberDialog` pattern, DNKADM-0479).
 */
import { request } from './client';

/** What the person does — the four UI roles (Owner / Manager / Front desk / Coach). */
export type TeamMemberKind = 'OWNER' | 'MANAGER' | 'FRONT_DESK' | 'COACH';

/** How a coach's booking calendar is set up. */
export type TeamMemberCalendarMode = 'CREATE' | 'EXISTING' | 'NONE';

/** The request body. `serviceIds` and `calendar` are sent for a COACH only. */
export interface CreateTeamMemberInput {
  name: string;
  email: string;
  kind: TeamMemberKind;
  /** v2 location ids (at least one). */
  locationIds: string[];
  /** COACH only; may be empty. */
  serviceIds?: string[];
  /** COACH only; absent means NONE. `calendarId` with EXISTING only. */
  calendar?: { mode: TeamMemberCalendarMode; calendarId?: string };
  idempotencyKey: string;
}

/** Step 5's outcome. An unknown future value is kept raw (rendered as "not linked"). */
export type TeamMemberCalendarStatus =
  | 'CREATED'
  | 'LINKED'
  | 'SKIPPED_NO_SCOPE'
  | 'SKIPPED_NO_GHL_USER'
  | 'FAILED'
  | 'NONE'
  | (string & {});

export interface TeamMemberInvitation {
  invitationId: string;
  email: string;
  role: string;
  /** False when the invite was created but the email did not go out. */
  emailSent: boolean;
}

/** The parsed `200`. */
export interface CreateTeamMemberResult {
  invitation: TeamMemberInvitation;
  kind: string;
  /** The new staff record (COACH only), else `null`. */
  staffId: string | null;
  calendar: { status: TeamMemberCalendarStatus; calendarId: string | null };
  /** The service ids the coach was added to. */
  servicesAdded: string[];
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/** A v2 id string as the wire's number (the endpoint takes `long` ids). */
function toWireId(id: string): number | string {
  const n = Number(id);
  return Number.isSafeInteger(n) ? n : id;
}

function parseResult(raw: unknown, input: CreateTeamMemberInput): CreateTeamMemberResult {
  const o = isObject(raw) ? raw : {};
  const inv = isObject(o['invitation']) ? o['invitation'] : {};
  const cal = isObject(o['calendar']) ? o['calendar'] : {};
  const servicesAdded = Array.isArray(o['servicesAdded'])
    ? o['servicesAdded'].map(asId).filter((id): id is string => id !== null)
    : [];
  return {
    invitation: {
      invitationId: asId(inv['invitationId']) ?? '',
      email: asText(inv['email']) ?? input.email,
      role: asText(inv['role']) ?? '',
      // Only an explicit `false` says the email did not go out.
      emailSent: inv['emailSent'] !== false,
    },
    kind: asText(o['kind']) ?? input.kind,
    staffId: asId(o['staffId']),
    calendar: { status: asText(cal['status']) ?? 'NONE', calendarId: asText(cal['calendarId']) },
    servicesAdded,
  };
}

/**
 * `POST /v2/admin/team-members` → every step's outcome. Throws a typed `ApiError` on a
 * refusal; the business `code` is on `details.code`, and a `409 INVITATION_ALREADY_*`
 * carries the current invite on `details.invitation`.
 */
export async function createTeamMember(
  input: CreateTeamMemberInput,
): Promise<CreateTeamMemberResult> {
  const json: Record<string, unknown> = {
    name: input.name,
    email: input.email,
    kind: input.kind,
    locationIds: input.locationIds.map(toWireId),
    idempotencyKey: input.idempotencyKey,
  };
  if (input.kind === 'COACH') {
    json['serviceIds'] = (input.serviceIds ?? []).map(toWireId);
    if (input.calendar) {
      const calendar: Record<string, unknown> = { mode: input.calendar.mode };
      if (input.calendar.mode === 'EXISTING' && input.calendar.calendarId !== undefined) {
        calendar['calendarId'] = input.calendar.calendarId;
      }
      json['calendar'] = calendar;
    }
  }
  const body = await request<unknown>('POST', '/v2/admin/team-members', { json });
  return parseResult(body, input);
}
